import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";
import { Loader2, Upload, ScanLine, Plus, X, Image as ImageIcon } from "lucide-react";
import { api } from "../lib/apiClient";
import { normalizeBlueprint, normalizeFloors } from "../lib/blueprint";
import { useFakeProgress } from "../lib/useFakeProgress";
import { toast } from "sonner";

const isImg = (f) => f && /(jpeg|jpg|png|webp)/i.test(f.type);

export default function PhotoReconstructor({ open, onOpenChange, onApply, onApplyFloors, city }) {
  const [inputMode, setInputMode] = useState("single"); // single | perfloor
  const [planMode, setPlanMode] = useState(false);       // floor-plan vs photo
  const [feat, setFeat] = useState({ garage: false, deck: false, porch: false });

  // single-image state
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [hints, setHints] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [floorsCount, setFloorsCount] = useState("");
  const [hasBasement, setHasBasement] = useState(false);

  // per-floor state
  const [floorPhotos, setFloorPhotos] = useState([{ file: null, preview: null, label: "Ground Floor" }]);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const { progress, elapsed, est, start, done, reset } = useFakeProgress();

  const featuresCsv = Object.entries(feat).filter(([, v]) => v).map(([k]) => k).join(",");

  const pick = (f) => {
    if (!f) return;
    if (!isImg(f)) { toast.error("Use JPEG, PNG or WEBP"); return; }
    setFile(f); setPreview(URL.createObjectURL(f)); setResult(null);
  };

  const addFloorSlot = () => {
    if (floorPhotos.length >= 4) { toast.info("Up to 4 floors"); return; }
    setFloorPhotos((p) => [...p, { file: null, preview: null, label: `Floor ${p.length + 1}` }]);
  };
  const removeFloorSlot = (i) => setFloorPhotos((p) => p.filter((_, idx) => idx !== i));
  const pickFloorPhoto = (i, f) => {
    if (!f) return;
    if (!isImg(f)) { toast.error("Use JPEG, PNG or WEBP"); return; }
    setFloorPhotos((p) => p.map((s, idx) => idx === i ? { ...s, file: f, preview: URL.createObjectURL(f) } : s));
    setResult(null);
  };
  const setFloorLabel = (i, v) => setFloorPhotos((p) => p.map((s, idx) => idx === i ? { ...s, label: v } : s));

  const reconstruct = async () => {
    const perFloor = inputMode === "perfloor";
    if (!perFloor && !file) { toast.error("Upload an image first"); return; }
    if (perFloor && !floorPhotos.some((s) => s.file)) { toast.error("Add at least one floor photo"); return; }

    setLoading(true); setResult(null); start(perFloor ? 70 : 50);
    try {
      let startRes;
      if (perFloor) {
        const slots = floorPhotos.filter((s) => s.file);
        const fd = new FormData();
        slots.forEach((s) => fd.append("files", s.file));
        const params = new URLSearchParams({ city, plan_mode: String(planMode) });
        params.set("floor_names", slots.map((s) => s.label || "Floor").join("|"));
        if (featuresCsv) params.set("features", featuresCsv);
        startRes = await api.post(`/ai/photo-to-blueprint-multi?${params.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        const fd = new FormData();
        fd.append("file", file);
        const params = new URLSearchParams({ city, plan_mode: String(planMode) });
        if (hints.trim()) params.set("hints", hints.trim());
        if (bedrooms) params.set("bedrooms", bedrooms);
        if (bathrooms) params.set("bathrooms", bathrooms);
        if (floorsCount) params.set("floors", floorsCount);
        if (hasBasement) params.set("basement", "true");
        if (featuresCsv) params.set("features", featuresCsv);
        startRes = await api.post(`/ai/photo-to-blueprint?${params.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const taskId = startRes.data.task_id;
      // poll until the async reconstruction finishes (avoids ingress timeouts on long vision calls)
      const deadline = Date.now() + 240000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await api.get(`/ai/photo-status/${taskId}`);
        if (st.data.status === "completed") {
          setResult(st.data.result);
          done();
          toast.success("Blueprint reconstructed");
          return;
        }
        if (st.data.status === "failed") {
          throw new Error(st.data.error || "Reconstruction failed");
        }
      }
      throw new Error("Timed out — please try again");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Reconstruction failed");
      reset();
    } finally { setLoading(false); }
  };

  const apply = () => {
    if (result.floors?.length && onApplyFloors) onApplyFloors(normalizeFloors(result), result);
    else onApply(normalizeBlueprint(result), result);
    onOpenChange(false);
    setResult(null); setFile(null); setPreview(null);
    setFloorPhotos([{ file: null, preview: null, label: "Ground Floor" }]);
  };

  const modeBtn = (id, label, cur, set) => (
    <button onClick={() => { set(id); setResult(null); }} data-testid={`photo-${id}-mode`}
      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${cur === id ? "bg-[#34D399] text-[#0D1117]" : "text-[#8B949E] hover:text-[#F0F6FC]"}`}>{label}</button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#161B22] border-[#30363D] text-[#F0F6FC] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-xl">
            <ScanLine className="w-5 h-5 text-[#34D399]" /> Photo → Blueprint
          </DialogTitle>
          <DialogDescription className="text-[#8B949E]">Upload a house photo or a top-down floor-plan sketch — add details and AI reconstructs a matching multi-floor blueprint.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* input mode + source type */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1 p-1 rounded-lg bg-[#0D1117] border border-[#30363D] w-fit">
              {modeBtn("single", "Single image", inputMode, setInputMode)}
              {modeBtn("perfloor", "One photo per floor", inputMode, setInputMode)}
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none ml-auto" data-testid="photo-planmode-label">
              <Checkbox checked={planMode} onCheckedChange={(v) => setPlanMode(!!v)} data-testid="photo-planmode-checkbox" className="border-[#30363D] data-[state=checked]:bg-[#38BDF8] data-[state=checked]:border-[#38BDF8]" />
              <span className="text-xs text-[#F0F6FC]">This is a <span className="text-[#38BDF8]">floor-plan sketch</span> (trace exactly)</span>
            </label>
          </div>

          {/* SINGLE MODE */}
          {inputMode === "single" && (
            <>
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}
                className="relative border-2 border-dashed border-[#30363D] rounded-xl p-6 text-center cursor-pointer hover:border-[#34D399] transition-colors bg-[#0D1117] min-h-[180px] flex flex-col items-center justify-center overflow-hidden"
                data-testid="photo-dropzone">
                <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="photo-dropzone-input" onChange={(e) => pick(e.target.files[0])} />
                {preview ? <img src={preview} alt="preview" className="max-h-40 rounded-md object-contain" /> : (
                  <>
                    <Upload className="w-8 h-8 text-[#8B949E] mb-2" />
                    <p className="text-sm text-[#8B949E]">Drop a {planMode ? "floor-plan sketch" : "house photo"} here</p>
                    <p className="text-xs text-[#6E7681] mt-1">JPEG · PNG · WEBP</p>
                  </>
                )}
                {loading && <div className="absolute inset-0 scanline pointer-events-none" />}
              </div>

              <div className="rounded-lg border border-[#30363D] bg-[#0D1117] p-3 space-y-3" data-testid="photo-details">
                <div className="text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad">Tell the AI about the house (optional but recommended)</div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-xs text-[#8B949E]">Bedrooms</Label><Input type="number" min="0" max="12" value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} placeholder="5" className="bg-[#161B22] border-[#30363D] h-9 font-mono-cad" data-testid="photo-bedrooms-input" /></div>
                  <div><Label className="text-xs text-[#8B949E]">Bathrooms</Label><Input type="number" min="0" max="12" value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} placeholder="3" className="bg-[#161B22] border-[#30363D] h-9 font-mono-cad" data-testid="photo-bathrooms-input" /></div>
                  <div><Label className="text-xs text-[#8B949E]">Floors</Label><Input type="number" min="1" max="4" value={floorsCount} onChange={(e) => setFloorsCount(e.target.value)} placeholder="3" className="bg-[#161B22] border-[#30363D] h-9 font-mono-cad" data-testid="photo-floors-input" /></div>
                </div>
                <div><Label className="text-xs text-[#8B949E]">Extra details</Label><Textarea value={hints} onChange={(e) => setHints(e.target.value)} placeholder="e.g. garage on the left, open kitchen to living, stairs in the center" className="bg-[#161B22] border-[#30363D] min-h-[56px] text-sm" data-testid="photo-hints-input" /></div>
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="photo-basement-label">
                  <Checkbox checked={hasBasement} onCheckedChange={(v) => setHasBasement(!!v)} data-testid="photo-basement-checkbox" className="border-[#30363D] data-[state=checked]:bg-[#34D399] data-[state=checked]:border-[#34D399]" />
                  <span className="text-sm text-[#F0F6FC]">Include a <span className="text-[#34D399]">basement</span> (walk-out lower level)</span>
                </label>
              </div>
            </>
          )}

          {/* PER-FLOOR MODE */}
          {inputMode === "perfloor" && (
            <div className="space-y-2" data-testid="perfloor-list">
              <p className="text-xs text-[#8B949E]">Add one image per level (bottom floor first). Each image is traced into its own floor.</p>
              {floorPhotos.map((s, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg border border-[#30363D] bg-[#0D1117] p-2" data-testid={`perfloor-slot-${i}`}>
                  <label className="w-16 h-14 shrink-0 rounded-md border border-dashed border-[#30363D] hover:border-[#34D399] cursor-pointer flex items-center justify-center overflow-hidden bg-[#161B22]">
                    <input type="file" accept="image/*" className="hidden" data-testid={`perfloor-input-${i}`} onChange={(e) => pickFloorPhoto(i, e.target.files[0])} />
                    {s.preview ? <img src={s.preview} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-[#6E7681]" />}
                  </label>
                  <Input value={s.label} onChange={(e) => setFloorLabel(i, e.target.value)} placeholder={`Floor ${i + 1} name`} className="bg-[#161B22] border-[#30363D] h-9" data-testid={`perfloor-label-${i}`} />
                  {floorPhotos.length > 1 && <button onClick={() => removeFloorSlot(i)} className="text-[#6E7681] hover:text-[#EF4444] px-1" data-testid={`perfloor-remove-${i}`}><X className="w-4 h-4" /></button>}
                </div>
              ))}
              {floorPhotos.length < 4 && (
                <Button variant="outline" onClick={addFloorSlot} data-testid="perfloor-add-btn" className="w-full border-[#30363D] border-dashed text-[#34D399] hover:bg-[#34D399]/10"><Plus className="w-4 h-4 mr-1.5" /> Add floor photo</Button>
              )}
              {loading && <div className="relative h-1"><div className="absolute inset-0 scanline pointer-events-none" /></div>}
            </div>
          )}

          {/* feature tags */}
          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-[#30363D] bg-[#0D1117] p-3" data-testid="photo-features">
            <span className="text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad">Include</span>
            {[["garage", "Garage"], ["deck", "Deck / Patio"], ["porch", "Porch"]].map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox checked={feat[k]} onCheckedChange={(v) => setFeat((s) => ({ ...s, [k]: !!v }))} data-testid={`photo-feature-${k}`} className="border-[#30363D] data-[state=checked]:bg-[#F59E0B] data-[state=checked]:border-[#F59E0B]" />
                <span className="text-sm text-[#F0F6FC]">{lbl}</span>
              </label>
            ))}
          </div>

          <Button data-testid="photo-reconstruct-btn" onClick={reconstruct} disabled={loading}
            className="w-full bg-[#34D399] hover:bg-[#10B981] text-[#0D1117] font-semibold">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> AI scanning image…</> : <><ScanLine className="w-4 h-4 mr-2" /> Reconstruct Blueprint</>}
          </Button>

          {loading && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2 bg-[#0D1117]" />
              <div className="flex justify-between text-xs text-[#8B949E] font-mono-cad">
                <span>Detecting rooms & structure… {Math.round(progress)}%</span>
                <span>{elapsed.toFixed(0)}s / ~{est}s</span>
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-[#30363D] bg-[#0D1117] p-4 space-y-2 animate-fade-up">
              <h4 className="font-heading font-semibold">{result.name}</h4>
              <p className="text-xs text-[#8B949E] leading-relaxed">{result.specs}</p>
              <div className="flex gap-3 text-xs font-mono-cad text-[#8B949E] flex-wrap">
                {result.floors?.length ? <span className="text-[#34D399]" data-testid="photo-floor-count">{result.floors.length} floor{result.floors.length > 1 ? "s" : ""} detected</span> : null}
                {result.floors?.length ? <span>·</span> : null}
                <span>{result.floors?.length ? result.floors.reduce((s, f) => s + (f.rooms?.length || 0), 0) : (result.rooms?.length || 0)} rooms detected</span>
                {result.total_area_sqft ? <><span>·</span><span>{result.total_area_sqft} ft²</span></> : null}
              </div>
              {result.floors?.length > 1 && (
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="photo-floor-list">
                  {result.floors.map((f, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-md bg-[#161B22] border border-[#30363D] text-[#34D399]">{f.name || `Floor ${i + 1}`}</span>)}
                </div>
              )}
              <Button data-testid="photo-apply-btn" onClick={apply} className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold">Load onto Canvas</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
